"use client";

import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { DayPicker, type Matcher } from "react-day-picker";
import {
  DateField,
  DateInput as AriaDateInput,
  DateSegment,
  I18nProvider,
} from "react-aria-components";
import { CalendarDate } from "@internationalized/date";
import { cn, formatToApiDate } from "@repo/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/**
 * `DateInputProps` doesn't type `id`/`data-testid`, but the underlying
 * `Group` element forwards arbitrary DOM/data attributes at runtime (see
 * `react-aria-components`'s `Group` implementation). Widen the type so we
 * can put the field id and e2e test id on the actual segmented control.
 */
const SegmentedDateInput = AriaDateInput as React.ComponentType<
  React.ComponentProps<typeof AriaDateInput> & {
    id?: string;
    "data-testid"?: string;
  }
>;

export interface DatePickerProps {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Retained for back-compat; the segmented field always renders MM/DD/YYYY. */
  dateFormat?: string;
  min?: Date;
  max?: Date;
  id?: string;
  name?: string;
  "data-testid"?: string;
}

/**
 * Sensible default year bounds so an entered 4-digit year has to "make sense".
 * Consumers can still constrain further via `min`/`max`. The lower bound also
 * doubles as the "year not fully typed yet" signal (e.g. 44, 198 are < 1900).
 */
const MIN_YEAR = 1900;
const MAX_YEAR_OFFSET = 100;

/** Convert a JS Date (local time) to a React Aria CalendarDate, or null. */
function toCalendarDate(value?: Date): CalendarDate | null {
  if (!value) return null;
  return new CalendarDate(
    value.getFullYear(),
    value.getMonth() + 1,
    value.getDate()
  );
}

/** Convert a React Aria CalendarDate back to a JS Date at local midnight. */
function toJsDate(value: CalendarDate | null): Date | undefined {
  if (!value) return undefined;
  return new Date(value.year, value.month - 1, value.day);
}

/** Format a JS Date as MM/DD/YYYY for user-facing messages. */
function formatUs(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${m}/${day}/${d.getFullYear()}`;
}

function DatePicker({
  date,
  onDateChange,
  placeholder: _placeholder = "MM/DD/YYYY",
  disabled = false,
  className,
  min,
  max,
  id,
  name,
  "data-testid": dataTestId,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  // Controlled by our own `current` state (NOT derived from the `date` prop —
  // that round-trip through formatToApiDate/parseDateString is lossy for
  // partial years and caused truncation). We mirror EVERY React Aria onChange
  // into `current` so RA's typing and backspace reflect natively (RA keeps its
  // multi-digit segment buffer). We emit `onDateChange` only for complete valid
  // dates. External `date` changes (form reset, edit-load, calendar pick) are
  // adopted via the effect below, guarded by `syncedKeyRef` so our own emitted
  // echoes don't clobber an in-progress edit.
  const [current, setCurrent] = React.useState<CalendarDate | null>(() =>
    toCalendarDate(date)
  );
  const syncedKeyRef = React.useRef<string>(date ? formatToApiDate(date) : "");

  React.useEffect(() => {
    const key = date ? formatToApiDate(date) : "";
    if (key === syncedKeyRef.current) return;
    syncedKeyRef.current = key;
    setCurrent(toCalendarDate(date));
  }, [date]);

  const minValue = toCalendarDate(min) ?? undefined;
  const maxValue = toCalendarDate(max) ?? undefined;

  const isOutOfRange = React.useCallback(
    (cd: CalendarDate | null): boolean => {
      if (!cd) return false;
      if (minValue && cd.compare(minValue) < 0) return true;
      if (maxValue && cd.compare(maxValue) > 0) return true;
      return false;
    },
    [minValue, maxValue]
  );

  // A complete, plausible 4-digit year within [MIN_YEAR, currentYear + 100].
  // Years below MIN_YEAR also cover the "still typing the year" states (e.g.
  // 4, 44, 198 → all < 1900), so the field reads as incomplete until finished.
  const maxYear = new Date().getFullYear() + MAX_YEAR_OFFSET;
  const hasSaneYear = React.useCallback(
    (cd: CalendarDate | null): boolean =>
      cd != null && cd.year >= MIN_YEAR && cd.year <= maxYear,
    [maxYear]
  );

  const handleChange = React.useCallback(
    (next: CalendarDate | null) => {
      setCurrent(next);
      if (!next) {
        // Field cleared. Mark "self" so the resulting empty prop echo isn't
        // treated as an external change that re-adopts the old value.
        syncedKeyRef.current = "";
        onDateChange?.(undefined);
        return;
      }
      // Only commit a complete, sane 4-digit year that's within range. Partial
      // years (44, 198), implausible ones (3025), and out-of-range dates are
      // NOT emitted (RA still displays them, flagged invalid below); we leave
      // syncedKeyRef untouched so the effect doesn't fight the in-progress edit.
      if (hasSaneYear(next) && !isOutOfRange(next)) {
        const js = toJsDate(next)!;
        syncedKeyRef.current = formatToApiDate(js);
        onDateChange?.(js);
      }
    },
    [onDateChange, hasSaneYear, isOutOfRange]
  );

  // Flag anything present that isn't a committable date: an incomplete/short
  // year ("11/11/44"), an implausible year ("11/11/3025"), or out of min/max.
  // Without this the field looks filled but holds no value with no feedback.
  const invalid =
    current != null && (!hasSaneYear(current) || isOutOfRange(current));

  // A specific, human message for why the value is invalid, so the field
  // explains itself instead of just turning red.
  const errorId = React.useId();
  const errorMessage = React.useMemo<string | null>(() => {
    if (!invalid || current == null) return null;
    if (!hasSaneYear(current)) {
      return `Enter a valid 4-digit year (${MIN_YEAR}–${maxYear})`;
    }
    if (minValue && current.compare(minValue) < 0 && min) {
      return `Date must be on or after ${formatUs(min)}`;
    }
    if (maxValue && current.compare(maxValue) > 0 && max) {
      return `Date must be on or before ${formatUs(max)}`;
    }
    return "Enter a valid date";
  }, [invalid, current, hasSaneYear, maxYear, minValue, maxValue, min, max]);

  const disabledMatchers = React.useMemo<Matcher[]>(() => {
    const matchers: Matcher[] = [];
    if (min) matchers.push({ before: min });
    if (max) matchers.push({ after: max });
    return matchers;
  }, [min, max]);

  return (
    <div className="w-full">
      {/*
       * The wrapper also receives `className` so layout-affecting utilities
       * (e.g. `flex-1` in a flex row) still apply to the element that's
       * actually the flex child. Sizing utilities (e.g. `h-8`, `h-9`) are
       * harmless here and are also applied directly to the segmented field
       * below so the visible control matches the requested size.
       */}
      <div className={cn("relative w-full", className)}>
        <I18nProvider locale="en-US">
          <DateField
            value={current}
            onChange={handleChange}
            minValue={minValue}
            maxValue={maxValue}
            isDisabled={disabled}
            isInvalid={invalid}
            granularity="day"
            shouldForceLeadingZeros
            aria-label="Date"
            name={name}
          >
            <SegmentedDateInput
              id={id}
              data-testid={dataTestId}
              aria-describedby={errorMessage ? errorId : undefined}
              className={cn(
                "flex h-10 w-full items-center rounded-xl border border-input bg-surface px-3 py-2 pr-10 text-sm shadow-sm",
                "focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20",
                invalid &&
                  "border-destructive focus-within:border-destructive focus-within:ring-destructive/20",
                disabled && "cursor-not-allowed opacity-50",
                className
              )}
            >
              {(segment) => (
                <DateSegment
                  segment={segment}
                  className={({ isPlaceholder, isFocused, isDisabled }) =>
                    cn(
                      "rounded px-0.5 tabular-nums outline-none",
                      isPlaceholder
                        ? "text-muted-foreground"
                        : "text-foreground",
                      isFocused && "bg-primary-soft text-primary",
                      isDisabled && "cursor-not-allowed opacity-50"
                    )
                  }
                />
              )}
            </SegmentedDateInput>
          </DateField>
        </I18nProvider>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled}
              aria-label="Open calendar"
              className="absolute right-0 top-0 h-10 w-10 text-muted-foreground hover:text-foreground"
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={date}
              defaultMonth={date ?? undefined}
              disabled={disabledMatchers}
              onSelect={(selectedDate) => {
                // Emit; the [date] effect adopts the new value and remounts RA
                // so the segments reflect the calendar pick.
                onDateChange?.(selectedDate);
                setOpen(false);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      {errorMessage ? (
        <p id={errorId} role="alert" className="mt-1 text-xs text-destructive">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-primary-soft/50 [&:has([aria-selected])]:bg-primary-soft first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-primary-soft hover:text-primary rounded-md cursor-pointer leading-9 text-center"
        ),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-primary-soft text-primary font-medium",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-primary-soft/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-primary-soft aria-selected:text-primary",
        day_hidden: "invisible",
        ...classNames,
      }}
      {...props}
    />
  );
}

export { DatePicker, Calendar };
